import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import PageLayout from "@/layouts/PageLayout";
import ScrollToTopButton from "@/components/ui/ScrollToTopButton";
import ScrollToTop from "@/components/ScrollToTop";
import AdminRoute from "@/components/routes/AdminRoute";

// Pages critiques (above the fold) — import statique OK
import Home from "@/pages/Home";
import Auth from "@/pages/Auth";
import AuthCallback from "@/components/AuthCallback";

// Toutes les autres — lazy loaded
const ChoixRole         = lazy(() => import("@/pages/ChoixRole"));
const Marketplace       = lazy(() => import("@/pages/Marketplace"));
const AssetDetail       = lazy(() => import("@/pages/AssetDetail"));
const SellerDashboard   = lazy(() => import("@/pages/SellerDashboard"));
const BuyerDashboard    = lazy(() => import("@/pages/BuyerDashboard"));
const AdminPanel        = lazy(() => import("@/pages/AdminPanel"));
const BecomeVendor      = lazy(() => import("@/pages/BecomeVendor"));
const PaymentSuccess    = lazy(() => import("@/pages/PaymentSuccess"));
const SellerProfile     = lazy(() => import("@/pages/SellerProfile"));
const CollectionDetail  = lazy(() => import("@/pages/CollectionDetail"));
const IAPage            = lazy(() => import("@/pages/IAPage"));
const AffiliateDashboard = lazy(() => import("@/pages/AffiliateDashboard"));
const FAQPage           = lazy(() => import("@/pages/FAQ"));
const CGV               = lazy(() => import("@/pages/legal/CGV"));
const Mentions          = lazy(() => import("@/pages/legal/Mentions"));
const Confidentialite   = lazy(() => import("@/pages/legal/Confidentialite"));
const CGU               = lazy(() => import("@/pages/legal/CGU"));
const LeStandard        = lazy(() => import("@/pages/LeStandard"));
const NotFound          = lazy(() => import("@/pages/NotFound"));

// Fallback de chargement
const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
    <div style={{ width: 32, height: 32, borderRadius: "50%", border: "2px solid #eee", borderTopColor: "#01696f", animation: "spin 0.7s linear infinite" }} />
  </div>
);

function AppRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/connexion"     element={<Auth />} />
        <Route path="/choisir-role"  element={<ChoixRole />} />

        <Route path="/vendeur/dashboard"     element={<SellerDashboard />} />
        <Route path="/acheteur/dashboard"    element={<BuyerDashboard />} />
        <Route path="/affiliation/dashboard" element={<AffiliateDashboard />} />
        <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />

        <Route element={<PageLayout />}>
          <Route path="/"                      element={<Home />} />
          <Route path="/marketplace"           element={<Marketplace />} />
          <Route path="/actif/:assetId"        element={<AssetDetail />} />
          <Route path="/devenir-createur"      element={<BecomeVendor />} />
          <Route path="/devenir-vendeur"       element={<Navigate to="/devenir-createur" replace />} />
          <Route path="/achat-reussi"          element={<PaymentSuccess />} />
          {/* Filet : d'anciennes sessions Stripe peuvent encore pointer ici. */}
          <Route path="/paiement/success"      element={<PaymentSuccess />} />
          <Route path="/vendeur/:sellerId"     element={<SellerProfile />} />
          <Route path="/bundle/:collectionId"  element={<CollectionDetail />} />
          <Route path="/vision"                element={<IAPage />} />
          <Route path="/ia-revolution"         element={<Navigate to="/vision" replace />} />
          <Route path="/pionniers"             element={<Navigate to="/vision#fondateurs" replace />} />
          <Route path="/le-standard"           element={<LeStandard />} />
          <Route path="/systeme-001"           element={<Navigate to="/le-standard" replace />} />
          <Route path="/faq"                   element={<FAQPage />} />
          <Route path="/legal/cgv"             element={<CGV />} />
          <Route path="/legal/mentions"        element={<Mentions />} />
          <Route path="/legal/confidentialite" element={<Confidentialite />} />
          <Route path="/legal/cgu"             element={<CGU />} />
          {/* Rendait <Home /> : toute URL erronée répondait 200 en affichant
              l'accueil, que Google indexait comme autant de copies (soft 404). */}
          <Route path="*"                      element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <AppRouter />
          <ScrollToTopButton />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
